import {
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';

import usePaymentProfile from '@/hooks/usePaymentProfile';
import useAuctionLiveStream, {
  buildBidSignature,
  deriveBidIdentifier,
  normalizeAuctionBid,
} from '@/pages/artwork/hooks/useAuctionLiveStream';
import useAuctionPaymentMethods from '@/pages/artwork/hooks/useAuctionPaymentMethods';
import auctionService from '@/services/auction.service';

function requiresPaymentAuthorization(payload) {
  if (!payload || typeof payload !== 'object') {
    return false;
  }

  const status = typeof payload.status === 'string' ? payload.status.trim().toLowerCase() : '';
  return payload.requires_payment_method === true
    || payload.requires_action === true
    || status === 'requires_payment_method'
    || status === 'requires_action';
}

function normaliseId(candidate) {
  if (candidate === null || candidate === undefined) {
    return null;
  }
  if (typeof candidate === 'string' || typeof candidate === 'number') {
    return String(candidate);
  }
  if (typeof candidate === 'object') {
    if (candidate === null) {
      return null;
    }
    if (Object.prototype.hasOwnProperty.call(candidate, '_id')) {
      return normaliseId(candidate._id);
    }
    if (Object.prototype.hasOwnProperty.call(candidate, 'id')) {
      return normaliseId(candidate.id);
    }
    if (typeof candidate.toString === 'function') {
      return candidate.toString();
    }
  }
  return null;
}

function buildPendingBidPayload({
  artworkId,
  amount,
  currency,
  idempotencyKey,
  paymentMethodId,
}) {
  const parsedAmount = Number(amount);
  if (!artworkId || !Number.isFinite(parsedAmount) || parsedAmount <= 0) {
    return null;
  }

  const randomSuffix = Math.random().toString(36).slice(2, 10);
  const baseKey = typeof idempotencyKey === 'string' && idempotencyKey.trim()
    ? `${idempotencyKey.trim()}-profile`
    : 'bid-profile';
  const derivedKey = `${baseKey}-${Date.now()}-${randomSuffix}`.slice(0, 120);

  const payload = {
    artwork_id: artworkId,
    amount: parsedAmount,
    currency: currency ?? null,
    idempotency_key: derivedKey,
  };

  if (typeof paymentMethodId === 'string' && paymentMethodId.trim()) {
    payload.payment_method_id = paymentMethodId.trim();
  }
  return payload;
}

/**
 * Mobile-friendly auction controller extracted from the desktop content section.
 * Keeps bid metadata in sync, listens to the SSE stream and exposes helpers usable
 * by both desktop and mobile auction views.
 *
 * NOTE: This duplicates logic from ContentSection for now. Once the desktop view
 * is migrated to consume this hook we can consolidate the implementations.
 */
export default function useArtworkAuctionController({
  artworkData: initialArtworkData,
  userSession = null,
  isActive = false,
} = {}) {
  const [artworkData, setArtworkData] = useState(initialArtworkData);
  const paymentProfile = usePaymentProfile();
  const paymentProfileStatus = paymentProfile?.status ?? 'unknown';
  const ensurePaymentProfile = paymentProfile?.ensurePaymentProfile;
  const handleAuthorizationChallenge = paymentProfile?.handleAuthorizationChallenge;
  const {
    paymentMethods,
    paymentMethodsStatus,
    paymentMethodsError,
    isLoadingPaymentMethods,
    selectedPaymentMethodId,
    setSelectedPaymentMethodId,
    loadPaymentMethods,
    refreshPaymentMethods,
    hasPaymentMethods,
  } = useAuctionPaymentMethods();

  useEffect(() => {
    setArtworkData(initialArtworkData);
  }, [initialArtworkData]);

  useEffect(() => {
    if (isActive && userSession) {
      loadPaymentMethods().catch(() => {});
    }
  }, [isActive, userSession, loadPaymentMethods]);

  const handleManagePaymentMethods = useCallback(async () => {
    if (typeof ensurePaymentProfile !== 'function') {
      return;
    }
    try {
      await ensurePaymentProfile({ forceSetup: true });
      await refreshPaymentMethods();
    }
    catch (error) {
      if (error?.code === 'BIDDER_PROFILE_CANCELLED') {
        return;
      }
      console.warn('[ARTWORK_AUCTION] Unable to open payment method manager', error);
    }
  }, [ensurePaymentProfile, refreshPaymentMethods]);

  const pendingBidRef = useRef(null);
  const recentBidRef = useRef(null);
  const bidderDetailsRef = useRef(new Map());
  const pendingTimersRef = useRef({ verify: null, cleanup: null });
  const [pendingBidState, setPendingBidState] = useState(null);

  const clearPendingTimers = useCallback(() => {
    const { verify, cleanup } = pendingTimersRef.current;
    if (verify) {
      clearTimeout(verify);
    }
    if (cleanup) {
      clearTimeout(cleanup);
    }
    pendingTimersRef.current = { verify: null, cleanup: null };
  }, []);

  const confirmPendingBid = useCallback(() => {
    clearPendingTimers();
    setPendingBidState((previous) => {
      if (previous?.status === 'confirmed') {
        return previous;
      }
      return {
        status: 'confirmed',
        message: 'Bid confirmed!',
      };
    });
    pendingTimersRef.current.cleanup = setTimeout(() => {
      setPendingBidState(null);
      pendingTimersRef.current.cleanup = null;
    }, 3000);
  }, [clearPendingTimers]);

  const markPendingBidWaiting = useCallback(() => {
    clearPendingTimers();
    setPendingBidState({
      status: 'waiting',
      message: 'Bid submitted. Waiting for live confirmation…',
    });

    pendingTimersRef.current.verify = setTimeout(() => {
      if (!pendingBidRef.current) {
        return;
      }
      setPendingBidState({
        status: 'pending_verification',
        message: 'Bid pending verification. We will update you shortly—no need to resubmit.',
      });
      pendingTimersRef.current.verify = null;
    }, 8000);
  }, [clearPendingTimers]);

  const doesCandidateMatchMeta = useCallback((candidateBid, meta) => {
    if (!candidateBid || !meta) {
      return false;
    }

    const candidateAmount = Number(candidateBid?.amount);
    const metaAmount = Number(meta?.amount);
    if (Number.isFinite(metaAmount)) {
      if (!Number.isFinite(candidateAmount) || Number(candidateAmount) !== metaAmount) {
        return false;
      }
    } else if (!Number.isFinite(candidateAmount)) {
      return false;
    }

    const candidateBidderId =
      candidateBid?.bidder?._id ??
      candidateBid?.bidder_id ??
      candidateBid?.bidderId ??
      candidateBid?.bidder?.id ??
      candidateBid?.bidder?.user_id ??
      null;
    const candidateUsername =
      candidateBid?.bidder_username ??
      candidateBid?.bidderUsername ??
      candidateBid?.bidder?.username ??
      candidateBid?.bidder?.user_username ??
      null;
    const candidateIdempotencyKey =
      candidateBid?.idempotency_key ??
      candidateBid?.idempotencyKey ??
      candidateBid?.bid?.idempotency_key ??
      candidateBid?.bid?.idempotencyKey ??
      null;

    if (
      meta.idempotencyKey &&
      candidateIdempotencyKey &&
      meta.idempotencyKey === candidateIdempotencyKey
    ) {
      return true;
    }

    const matchesId =
      meta.bidderId &&
      candidateBidderId &&
      meta.bidderId === candidateBidderId;
    const matchesUsername =
      meta.bidderUsername &&
      candidateUsername &&
      meta.bidderUsername === candidateUsername;

    if (!matchesId && !matchesUsername) {
      const hasIdentity = Boolean(candidateBidderId || candidateUsername || candidateIdempotencyKey);
      if (hasIdentity) {
        return false;
      }
    }

    return true;
  }, []);

  const extractBidMeta = useCallback((bid) => {
    if (!bid) {
      return null;
    }
    const candidate = bid.bid ?? bid;
    const amount = Number(candidate?.amount);

    const bidder = candidate?.bidder && typeof candidate.bidder === 'object' ? candidate.bidder : null;
    const bidderId =
      candidate?.bidderId ??
      candidate?.bidder_id ??
      bidder?._id ??
      bidder?.id ??
      bidder?.user_id ??
      null;
    const bidderUsername =
      candidate?.bidderUsername ??
      candidate?.bidder_username ??
      bidder?.user_username ??
      bidder?.username ??
      bidder?.bidder_username ??
      null;
    const bidderType =
      candidate?.bidderType ??
      candidate?.bidder_type ??
      bidder?.user_type ??
      bidder?.type ??
      bidder?.user_type ??
      null;
    const bidderImage =
      candidate?.bidder_image_id ??
      candidate?.bidderImageId ??
      bidder?.user_image?.image_id ??
      bidder?.gallery_image?.image_id ??
      bidder?.artist_image?.image_id ??
      (typeof bidder?.user_image === 'string' ? bidder?.user_image : null) ??
      null;
    const idempotencyKey =
      candidate?.idempotency_key ??
      candidate?.idempotencyKey ??
      null;

    return {
      amount: Number.isFinite(amount) ? amount : null,
      bidderId: bidderId || null,
      bidderUsername: bidderUsername || null,
      bidderType: bidderType || null,
      bidderImageId: bidderImage || null,
      idempotencyKey: idempotencyKey || null,
    };
  }, []);

  const getBidderCacheKey = useCallback((meta) => {
    if (!meta) {
      return null;
    }
    const id = meta.bidderId;
    const username = meta.bidderUsername;
    if (id) {
      return `id:${id}`;
    }
    if (username) {
      return `username:${username}`;
    }
    if (meta.idempotencyKey) {
      return `idempotency:${meta.idempotencyKey}`;
    }
    if (meta.bidderImageId) {
      return `avatar:${meta.bidderImageId}`;
    }
    return null;
  }, []);

  const registerBidderDetails = useCallback((meta) => {
    if (!meta) {
      return;
    }
    const key = getBidderCacheKey(meta);
    if (!key) {
      return;
    }
    const previous = bidderDetailsRef.current.get(key) || {};
    const merged = {
      ...previous,
      ...meta,
      amount: Number.isFinite(meta.amount) ? meta.amount : previous.amount ?? null,
    };
    bidderDetailsRef.current.set(key, merged);
  }, [getBidderCacheKey]);

  const registerBidderSummaries = useCallback((summary) => {
    if (!summary || typeof summary !== 'object') {
      return;
    }

    const entries = Array.isArray(summary)
      ? summary
      : Object.values(summary);

    entries
      .filter(Boolean)
      .forEach((entry) => {
        const meta = extractBidMeta({
          amount: entry?.amount ?? entry?.latest_bid_amount,
          bidder: entry?.bidder,
          bidder_id: entry?.bidder_id ?? entry?.bidderId,
          bidder_username: entry?.bidder_username ?? entry?.bidderUsername,
          bidder_type: entry?.bidder_type ?? entry?.bidderType,
          bidder_image_id: entry?.bidder_image_id ?? entry?.bidderImageId,
        });
        registerBidderDetails(meta);
      });
  }, [extractBidMeta, registerBidderDetails]);

  const applyMetaToBid = useCallback((bid, meta) => {
    if (!bid || !meta) {
      return bid;
    }

    const nextBid = { ...bid };
    const baseMeta = extractBidMeta(nextBid);
    let mutated = false;

    if (baseMeta?.idempotencyKey && meta.idempotencyKey === baseMeta.idempotencyKey) {
      // Already enriched with the pending metadata.
    }

    if (meta.amount !== null && Number(nextBid.amount) !== meta.amount) {
      nextBid.amount = meta.amount;
      mutated = true;
    }

    if (meta.idempotencyKey && nextBid.idempotency_key !== meta.idempotencyKey) {
      nextBid.idempotency_key = meta.idempotencyKey;
      nextBid.idempotencyKey = meta.idempotencyKey;
      mutated = true;
    }

    if (meta.bidderId && nextBid.bidder_id !== meta.bidderId) {
      nextBid.bidder_id = meta.bidderId;
      mutated = true;
    }
    if (meta.bidderUsername && nextBid.bidder_username !== meta.bidderUsername) {
      nextBid.bidder_username = meta.bidderUsername;
      mutated = true;
    }
    if (meta.bidderType && nextBid.bidder_type !== meta.bidderType) {
      nextBid.bidder_type = meta.bidderType;
      mutated = true;
    }
    if (meta.bidderImageId && nextBid.bidder_image_id !== meta.bidderImageId) {
      nextBid.bidder_image_id = meta.bidderImageId;
      mutated = true;
    }

    let bidderMutated = false;
    let nextBidder = nextBid.bidder ? { ...nextBid.bidder } : null;
    const ensureBidder = () => {
      if (!nextBidder) {
        nextBidder = {};
      }
    };
    const assignBidderField = (key, value) => {
      if (value === undefined || value === null) {
        return;
      }
      ensureBidder();
      if (nextBidder[key] === value) {
        return;
      }
      nextBidder[key] = value;
      bidderMutated = true;
    };

    assignBidderField('_id', meta.bidderId);
    assignBidderField('id', meta.bidderId);
    assignBidderField('user_id', meta.bidderId);
    assignBidderField('user_username', meta.bidderUsername);
    assignBidderField('username', meta.bidderUsername);
    assignBidderField('bidder_username', meta.bidderUsername);
    assignBidderField('user_type', meta.bidderType);
    assignBidderField('bidder_type', meta.bidderType);

    if (meta.bidderImageId) {
      ensureBidder();
      const currentImage = nextBidder.user_image ? { ...nextBidder.user_image } : {};
      if (currentImage.image_id !== meta.bidderImageId) {
        currentImage.image_id = meta.bidderImageId;
        bidderMutated = true;
      }
      nextBidder.user_image = currentImage;
    }

    if (bidderMutated) {
      nextBid.bidder = nextBidder;
      mutated = true;
    }

    return mutated ? nextBid : bid;
  }, [extractBidMeta]);

  const enhanceBidWithFallbacks = useCallback((candidate, additionalSources = []) => {
    if (!candidate) {
      return candidate;
    }

    let result = candidate;
    const candidateMeta = extractBidMeta(candidate);
    const cacheKey = getBidderCacheKey(candidateMeta);
    const cachedMeta = cacheKey ? bidderDetailsRef.current.get(cacheKey) : null;

    const metaSources = [
      extractBidMeta(pendingBidRef.current),
      extractBidMeta(recentBidRef.current),
      cachedMeta,
      ...additionalSources.map(extractBidMeta),
    ].filter(Boolean);

    metaSources.forEach((meta) => {
      if (!meta) {
        return;
      }
      if (!doesCandidateMatchMeta(result, meta)) {
        return;
      }
      const enriched = applyMetaToBid(result, meta);
      if (enriched !== result) {
        result = enriched;
      }
    });

    registerBidderDetails(extractBidMeta(result));

    return result;
  }, [applyMetaToBid, doesCandidateMatchMeta, extractBidMeta, getBidderCacheKey, registerBidderDetails]);

  const resolvePendingBidMatch = useCallback((candidateBid) => {
    if (!candidateBid) {
      return false;
    }

    const sources = [
      { ref: pendingBidRef, meta: pendingBidRef.current },
      { ref: recentBidRef, meta: recentBidRef.current },
    ];

    let matchedMeta = null;
    sources.some((source) => {
      if (!source.meta) {
        return false;
      }
      if (!doesCandidateMatchMeta(candidateBid, source.meta)) {
        return false;
      }
      matchedMeta = source.meta;
      return true;
    });

    if (!matchedMeta) {
      return false;
    }

    confirmPendingBid();

    [pendingBidRef, recentBidRef].forEach((ref) => {
      const current = ref.current;
      if (!current) {
        return;
      }

      if (current === matchedMeta) {
        ref.current = null;
        return;
      }

      if (
        matchedMeta.idempotencyKey &&
        current.idempotencyKey &&
        matchedMeta.idempotencyKey === current.idempotencyKey
      ) {
        ref.current = null;
        return;
      }

      const currentAmount = Number(current?.amount);
      const matchedAmount = Number(matchedMeta?.amount);
      if (!Number.isFinite(currentAmount) || currentAmount !== matchedAmount) {
        return;
      }

      const currentBidderId = current?.bidderId ?? null;
      const matchedBidderId = matchedMeta?.bidderId ?? null;
      const currentBidderUsername = current?.bidderUsername ?? null;
      const matchedBidderUsername = matchedMeta?.bidderUsername ?? null;

      const sameId = currentBidderId && matchedBidderId && currentBidderId === matchedBidderId;
      const sameUsername =
        currentBidderUsername &&
        matchedBidderUsername &&
        currentBidderUsername === matchedBidderUsername;

      if (sameId || sameUsername || (!currentBidderId && !currentBidderUsername)) {
        ref.current = null;
      }
    });

    return true;
  }, [confirmPendingBid, doesCandidateMatchMeta]);

  useEffect(() => () => {
    clearPendingTimers();
    pendingBidRef.current = null;
    recentBidRef.current = null;
    bidderDetailsRef.current.clear();
  }, [clearPendingTimers]);

  useEffect(() => {
    if (!isActive) {
      pendingBidRef.current = null;
      recentBidRef.current = null;
      bidderDetailsRef.current.clear();
      clearPendingTimers();
      setPendingBidState(null);
    }
  }, [clearPendingTimers, isActive]);

  useEffect(() => {
    const auction = artworkData?.artwork_auction ?? null;
    if (!auction) {
      return;
    }

    if (Array.isArray(auction.bids)) {
      auction.bids
        .map(extractBidMeta)
        .forEach(registerBidderDetails);
    }
    if (auction.current_bid) {
      registerBidderDetails(extractBidMeta(auction.current_bid));
    }
    if (auction.bidder_summary) {
      registerBidderSummaries(auction.bidder_summary);
    }
  }, [artworkData?.artwork_auction, extractBidMeta, registerBidderDetails, registerBidderSummaries]);

  const applyAuctionUpdate = useCallback(({
    bid = null,
    auctionPatch = null,
    aboutPatch = null,
    replaceBids = undefined,
  } = {}) => {
    const candidateBids = [];
    const candidateIds = new Set();
    const registerCandidate = (candidate) => {
      if (!candidate) {
        return;
      }
      registerBidderDetails(extractBidMeta(candidate));
      const identifier = deriveBidIdentifier(candidate);
      if (identifier) {
        if (candidateIds.has(identifier)) {
          return;
        }
        candidateIds.add(identifier);
      }
      candidateBids.push(candidate);
    };

    setArtworkData((previous) => {
      if (!previous) {
        return previous;
      }

      const previousAuction = previous.artwork_auction ?? {};
      const previousAbout = previous.artwork_about ?? {};

      const additionalSources = [];
      if (Array.isArray(previousAuction.bids)) {
        additionalSources.push(...previousAuction.bids);
      }
      if (previousAuction.current_bid) {
        additionalSources.push(previousAuction.current_bid);
      }
      if (previousAuction.bidder_summary) {
        registerBidderSummaries(previousAuction.bidder_summary);
      }

      let auctionChanged = false;
      let aboutChanged = false;
      let nextAuction = previousAuction;
      let nextAbout = previousAbout;

      const ensureAuctionMutable = () => {
        if (!auctionChanged) {
          auctionChanged = true;
          nextAuction = { ...previousAuction };
        }
      };

      const ensureAboutMutable = () => {
        if (!aboutChanged) {
          aboutChanged = true;
          nextAbout = { ...previousAbout };
        }
      };

      const enhanceBid = (candidate) => enhanceBidWithFallbacks(candidate, additionalSources);

      if (typeof replaceBids !== 'undefined') {
        ensureAuctionMutable();
        nextAuction.bids = Array.isArray(replaceBids)
          ? replaceBids.map((candidate) => enhanceBid(normalizeAuctionBid(candidate))).filter(Boolean)
          : [];
        const signature = buildBidSignature(nextAuction.bids);
        nextAuction.bids_signature = signature;
      } else if (bid) {
        ensureAuctionMutable();
        const normalizedBid = enhanceBid(normalizeAuctionBid(bid));
        if (normalizedBid) {
          const signatureBefore = buildBidSignature(previousAuction.bids);
          const candidateList = Array.isArray(nextAuction.bids) ? [...nextAuction.bids] : [];
          candidateList.unshift(normalizedBid);
          const signatureAfter = buildBidSignature(candidateList);
          if (signatureAfter !== signatureBefore) {
            nextAuction.bids = candidateList;
            nextAuction.bids_signature = signatureAfter;
          }
          registerCandidate(normalizedBid);
        }
      }

      const readFromSource = (source, keys) => {
        if (!source) {
          return undefined;
        }
        for (const key of keys) {
          if (source[key] !== undefined) {
            return source[key];
          }
        }
        return undefined;
      };

      if (auctionPatch) {
        const auctionMap = [
          ['current_bid_amount', ['current_bid_amount', 'currentBidAmount']],
          ['current_bid', ['current_bid', 'currentBid']],
          ['highest_bid_amount', ['highest_bid_amount', 'highestBidAmount']],
          ['highest_bid', ['highest_bid', 'highestBid']],
          ['total_bids', ['total_bids', 'totalBids']],
          ['is_enabled', ['is_enabled', 'isEnabled']],
          ['state', ['state', 'status']],
          ['is_closed', ['is_closed', 'isClosed', 'closed']],
          ['ends_at', ['ends_at', 'endsAt']],
          ['starts_at', ['starts_at', 'startsAt']],
          ['bidder_summary', ['bidder_summary', 'bidderSummary', 'current_bidder_summary', 'currentBidderSummary']],
          ['currency', ['currency']],
          ['price_starting_at', ['price_starting_at', 'priceStartingAt']],
          ['bids_total', ['bids_total', 'bidsTotal']],
        ];

        auctionMap.forEach(([targetKey, keys]) => {
          const value = readFromSource(auctionPatch, keys);
          if (value !== undefined && value !== nextAuction[targetKey]) {
            ensureAuctionMutable();
            nextAuction[targetKey] = value;
          }
        });

        const patchCurrentBid = readFromSource(auctionPatch, ['current_bid', 'currentBid']);
        if (patchCurrentBid) {
          const normalizedCurrentBid = enhanceBid(normalizeAuctionBid(patchCurrentBid));
          if (normalizedCurrentBid) {
            registerCandidate(normalizedCurrentBid);
            const prevCurrent = previousAuction.current_bid ?? null;
            const prevCurrentId = deriveBidIdentifier(prevCurrent);
            const nextCurrentId = deriveBidIdentifier(normalizedCurrentBid);
            if (
              prevCurrentId !== nextCurrentId ||
              Number(prevCurrent?.amount) !== Number(normalizedCurrentBid.amount)
            ) {
              ensureAuctionMutable();
              nextAuction.current_bid = normalizedCurrentBid;
            }
          }
        }

        const patchHighestBid = readFromSource(auctionPatch, ['highest_bid', 'highestBid']);
        if (patchHighestBid) {
          const normalizedHighestBid = enhanceBid(normalizeAuctionBid(patchHighestBid));
          if (normalizedHighestBid) {
            registerCandidate(normalizedHighestBid);
            const prevHighest = previousAuction.highest_bid ?? null;
            const prevHighestId = deriveBidIdentifier(prevHighest);
            const nextHighestId = deriveBidIdentifier(normalizedHighestBid);
            if (
              prevHighestId !== nextHighestId ||
              Number(prevHighest?.amount) !== Number(normalizedHighestBid.amount)
            ) {
              ensureAuctionMutable();
              nextAuction.highest_bid = normalizedHighestBid;
            }
          }
        }

        if (auctionPatch.bids && replaceBids === undefined) {
          ensureAuctionMutable();
          nextAuction.bids = Array.isArray(auctionPatch.bids)
            ? auctionPatch.bids.map((candidate) => enhanceBid(normalizeAuctionBid(candidate))).filter(Boolean)
            : [];
          nextAuction.bids_signature = buildBidSignature(nextAuction.bids);
        }

        const bidderSummaryPatch = readFromSource(auctionPatch, ['bidder_summary', 'bidderSummary', 'current_bidder_summary', 'currentBidderSummary']);
        if (bidderSummaryPatch) {
          registerBidderSummaries(bidderSummaryPatch);
        }
      }

      if (aboutPatch) {
        const aboutMap = [
          ['final_bid_price', ['final_bid_price', 'finalBidPrice']],
        ];

        aboutMap.forEach(([targetKey, keys]) => {
          const value = readFromSource(aboutPatch, keys);
          if (value !== undefined && value !== nextAbout[targetKey]) {
            ensureAboutMutable();
            nextAbout[targetKey] = value;
          }
        });
      }

      if (!auctionChanged && !aboutChanged) {
        return previous;
      }

      return {
        ...previous,
        ...(aboutChanged ? { artwork_about: nextAbout } : {}),
        ...(auctionChanged ? { artwork_auction: nextAuction } : {}),
      };
    });

    if (candidateBids.length > 0) {
      candidateBids.forEach(resolvePendingBidMatch);
    }
  }, [enhanceBidWithFallbacks, extractBidMeta, registerBidderDetails, registerBidderSummaries, resolvePendingBidMatch]);

  const handleAuctionStreamEvent = useCallback((event) => {
    if (!event) {
      return;
    }
    const payload = event.payload ?? {};
    const auctionPatch = payload.auction ?? payload.auctionState ?? null;
    const aboutPatch = payload.about ?? payload.artwork_about ?? null;

    if (event.type === 'bid') {
      applyAuctionUpdate({
        bid: payload.bid ?? payload.latestBid ?? payload,
        auctionPatch,
        aboutPatch,
      });
    } else if (event.type === 'meta') {
      applyAuctionUpdate({
        auctionPatch,
        aboutPatch,
        replaceBids: Array.isArray(auctionPatch?.bids) ? auctionPatch.bids : undefined,
      });
    } else if (event.type === 'close') {
      applyAuctionUpdate({
        auctionPatch: {
          ...auctionPatch,
          is_enabled: false,
          state: (auctionPatch && (auctionPatch.state || auctionPatch.status)) || 'closed',
        },
        aboutPatch,
      });
    } else {
      applyAuctionUpdate({
        auctionPatch,
        aboutPatch,
      });
    }
  }, [applyAuctionUpdate]);

  const {
    connectionBadge,
    isCountdownFrozen,
    serverTimeOffsetMs,
  } = useAuctionLiveStream({
    artworkId: artworkData?._id,
    isAuctionEnabled: Boolean(artworkData?.artwork_auction?.is_enabled),
    shouldConnect: Boolean(isActive && artworkData?._id),
    onEvent: handleAuctionStreamEvent,
    userId: userSession?._id,
  });

  const auctionDetails = artworkData?.artwork_auction ?? {};
  const isAuctionArtwork = Boolean(artworkData?.artwork_about?.artwork_is_auction);
  const isAuctionEnabled = Boolean(auctionDetails?.is_enabled);
  const hasAuctionClosedFlag = Boolean(auctionDetails?.is_closed ?? auctionDetails?.closed);
  const endsAtTimestamp = auctionDetails?.ends_at ? new Date(auctionDetails.ends_at).getTime() : NaN;
  const hasEndedBySchedule = Number.isFinite(endsAtTimestamp) && endsAtTimestamp <= Date.now();
  const auctionStateRaw = typeof auctionDetails?.state === 'string'
    ? auctionDetails.state.trim().toLowerCase()
    : typeof auctionDetails?.status === 'string'
      ? auctionDetails.status.trim().toLowerCase()
      : '';
  const isAuctionClosed = Boolean(
    hasAuctionClosedFlag ||
    auctionStateRaw === 'closed' ||
    auctionStateRaw === 'ended' ||
    (hasEndedBySchedule && !isAuctionEnabled)
  );

  const viewerId = normaliseId(userSession?._id ?? userSession?.id ?? null);
  const galleryOwnerId = normaliseId(
    artworkData?.artwork_about?.artwork_gallery?._id
    ?? artworkData?.artwork_about?.artwork_gallery
    ?? null,
  );
  const artworkCreatorId = normaliseId(artworkData?.artwork_about?.artwork_artist);
  const viewerTypeRaw = userSession?.user_type ?? userSession?.__t ?? null;
  const viewerType = typeof viewerTypeRaw === 'string' ? viewerTypeRaw.trim().toLowerCase() : '';
  const isGalleryViewer = viewerType === 'gallery';

  const isOwnArtwork = Boolean(viewerId && galleryOwnerId && viewerId === galleryOwnerId);
  const isArtworkCreator = Boolean(viewerId && artworkCreatorId && viewerId === artworkCreatorId);
  const isBiddingBlocked = isGalleryViewer || isOwnArtwork || isArtworkCreator;

  const handlePlaceBid = useCallback(async (rawInput) => {
    const payload = (rawInput && typeof rawInput === 'object')
      ? rawInput
      : { amount: rawInput };
    const amount = payload?.amount;
    const paymentMethodOverride =
      typeof payload?.paymentMethodId === 'string' && payload.paymentMethodId.trim()
        ? payload.paymentMethodId.trim()
        : null;

    if (!artworkData?._id) {
      throw new Error('Artwork information is unavailable.');
    }

    if (isBiddingBlocked) {
      let restrictionMessage = 'You are not allowed to place bids on this auction.';
      if (isGalleryViewer) {
        restrictionMessage = 'Gallery accounts cannot place bids.';
      }
      else if (isArtworkCreator) {
        restrictionMessage = 'Artists cannot bid on their own artworks.';
      }
      else if (isOwnArtwork) {
        restrictionMessage = 'Your gallery cannot bid on this auction.';
      }
      const restrictionError = new Error(restrictionMessage);
      restrictionError.code = 'BIDDER_NOT_ELIGIBLE';
      throw restrictionError;
    }

    const bidderCountryHint =
      userSession?.collector_profile?.address_country ??
      userSession?.gallery_address?.country ??
      userSession?.user_location?.country ??
      userSession?.user_country ??
      null;

    if (typeof ensurePaymentProfile === 'function' && paymentProfileStatus !== 'verified') {
      try {
        await ensurePaymentProfile({ countryHint: bidderCountryHint ?? undefined });
      }
      catch (profileError) {
        if (profileError?.code === 'BIDDER_PROFILE_CANCELLED') {
          return null;
        }
        throw profileError;
      }
    }

    let resolvedPaymentMethodId = paymentMethodOverride ?? selectedPaymentMethodId ?? null;
    if (!resolvedPaymentMethodId) {
      try {
        const loadResult = await loadPaymentMethods({ force: true });
        const fallbackId =
          loadResult?.selectedPaymentMethodId ??
          loadResult?.defaultPaymentMethodId ??
          (Array.isArray(loadResult?.methods) ? loadResult.methods[0]?.id : null);
        resolvedPaymentMethodId = paymentMethodOverride ?? selectedPaymentMethodId ?? fallbackId ?? null;
      }
      catch (methodsError) {
        console.warn('[ARTWORK_AUCTION] Unable to load payment methods before bidding', methodsError);
      }
    }

    if (!resolvedPaymentMethodId) {
      const methodError = new Error('Add a payment method before placing your bid.');
      methodError.code = 'PAYMENT_METHOD_REQUIRED';
      throw methodError;
    }

    const bidCurrency = artworkData?.artwork_about?.artwork_currency;
    const idempotencyKey = (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function')
      ? crypto.randomUUID()
      : `${Date.now()}-${Math.random().toString(16).slice(2)}`;

    const pendingBidPayload = buildPendingBidPayload({
      artworkId: artworkData._id,
      amount,
      currency: bidCurrency,
      idempotencyKey,
      paymentMethodId: resolvedPaymentMethodId,
    });

    const bidderId = userSession?._id ?? null;
    const bidderUsername = userSession?.user_username ?? null;
    const bidderType = userSession?.user_type ?? null;
    let bidderImageId = null;
    const userImage = userSession?.user_image ?? null;
    if (userImage && typeof userImage === 'object' && userImage.image_id) {
      bidderImageId = userImage.image_id;
    } else if (typeof userImage === 'string' && userImage) {
      bidderImageId = userImage;
    } else if (userSession?.profile_image?.image_id) {
      bidderImageId = userSession.profile_image.image_id;
    } else if (userSession?.gallery_image?.image_id) {
      bidderImageId = userSession.gallery_image.image_id;
    } else if (userSession?.artist_image?.image_id) {
      bidderImageId = userSession.artist_image.image_id;
    }

    const pendingMetadata = {
      idempotencyKey,
      amount: Number(amount),
      bidderId,
      bidderUsername,
      bidderType,
      bidderImageId,
      createdAt: Date.now(),
      paymentMethodId: resolvedPaymentMethodId,
    };
    pendingBidRef.current = pendingMetadata;
    recentBidRef.current = pendingMetadata;
    registerBidderDetails(pendingMetadata);
    markPendingBidWaiting();

    const updatePendingBidIdempotencyKey = (nextKey) => {
      if (!nextKey) {
        return;
      }
      if (pendingBidRef.current) {
        pendingBidRef.current.idempotencyKey = nextKey;
      }
      if (recentBidRef.current) {
        recentBidRef.current.idempotencyKey = nextKey;
      }
    };

    try {
      const prepareResult = await auctionService.prepareBidAuthorization({
        artworkId: artworkData._id,
        amount,
        currency: bidCurrency,
        placedFrom: 'platform',
        idempotencyKey,
        paymentMethodId: resolvedPaymentMethodId,
      });
      const { response: prepareResponse, data: prepareData } = prepareResult;

      let authorization = null;

      if (!prepareResponse.ok) {
        const errorMessage = prepareData?.error_message || 'Unable to prepare the bid authorization.';
        const error = new Error(errorMessage);
        error.response = prepareResponse;
        error.data = prepareData;

        let recoveredAuthorization = null;
        const requiresProfileRecovery =
          typeof ensurePaymentProfile === 'function' &&
          (
            prepareData?.error_type === 'BIDDER_PROFILE_NOT_VERIFIED' ||
            prepareData?.error_type === 'BIDDER_PROFILE_REQUIRES_PAYMENT_METHOD'
          );

        if (requiresProfileRecovery) {
          const profileResult = await ensurePaymentProfile({
            countryHint: bidderCountryHint ?? undefined,
            forceSetup: true,
            pendingBid: pendingBidPayload ?? undefined,
          });

          if (profileResult?.pending_bid_authorization?.id) {
            recoveredAuthorization = profileResult.pending_bid_authorization;
            updatePendingBidIdempotencyKey(pendingBidPayload?.idempotency_key);
          }
          else if (profileResult?.pending_bid_error) {
            const pendingErrorMessage =
              profileResult.pending_bid_error.message ??
              'Unable to verify your payment method for this bid.';
            const pendingError = new Error(pendingErrorMessage);
            pendingError.code =
              profileResult.pending_bid_error.code ?? 'PENDING_BID_AUTHORIZATION_FAILED';
            throw pendingError;
          }
        }

        if (!recoveredAuthorization) {
          pendingBidRef.current = null;
          recentBidRef.current = null;
          clearPendingTimers();
          setPendingBidState(null);
          throw error;
        }

        authorization = recoveredAuthorization;
      }
      else {
        if (prepareData?.bid && prepareData?.replayed) {
          confirmPendingBid();
          pendingBidRef.current = null;
          recentBidRef.current = null;
          return prepareData;
        }

        authorization = prepareData?.payment_authorization ?? null;
        if (!authorization?.id) {
          pendingBidRef.current = null;
          recentBidRef.current = null;
          clearPendingTimers();
          setPendingBidState(null);
          throw new Error('Unable to prepare the payment authorization. Please try again.');
        }
      }

      if (requiresPaymentAuthorization(authorization) && typeof handleAuthorizationChallenge === 'function') {
        const challengeResult = await handleAuthorizationChallenge({
          ...authorization,
          amount,
          currency: bidCurrency,
          ...(pendingBidPayload ? { pending_bid: pendingBidPayload } : {}),
        });

        updatePendingBidIdempotencyKey(pendingBidPayload?.idempotency_key);

        if (pendingBidPayload) {
          if (challengeResult?.pending_bid_authorization?.id) {
            authorization = challengeResult.pending_bid_authorization;
          }
          else if (requiresPaymentAuthorization(authorization)) {
            const challengeErrorMessage =
              challengeResult?.pending_bid_error?.message ??
              'Unable to verify your payment method for this bid. Please try again.';
            const challengeError = new Error(challengeErrorMessage);
            challengeError.code = challengeResult?.pending_bid_error?.code ?? 'PENDING_BID_AUTHORIZATION_FAILED';
            throw challengeError;
          }
        }
      }

      const { response, data } = await auctionService.placeBid({
        artworkId: artworkData._id,
        authorizationId: authorization.id,
        placedFrom: 'platform',
        idempotencyKey,
      });

      if (!response.ok) {
        pendingBidRef.current = null;
        recentBidRef.current = null;
        clearPendingTimers();
        setPendingBidState(null);
        const errorMessage = data?.error_message || 'Unable to place bid. Please try again.';
        const error = new Error(errorMessage);
        error.response = response;
        error.data = data;
        throw error;
      }

      confirmPendingBid();
      if (data?.payment_authorization && requiresPaymentAuthorization(data.payment_authorization) && typeof handleAuthorizationChallenge === 'function') {
        try {
          await handleAuthorizationChallenge({
            ...data.payment_authorization,
            amount,
            currency: bidCurrency,
          });
        }
        catch (authorizationError) {
          console.warn('[ARTWORK_AUCTION] Failed to confirm payment authorization', authorizationError);
        }
      }
      if (pendingBidRef.current && pendingBidRef.current.idempotencyKey === idempotencyKey) {
        pendingBidRef.current = null;
      }
      if (recentBidRef.current && recentBidRef.current.idempotencyKey === idempotencyKey) {
        recentBidRef.current.confirmedViaHttp = true;
      }

      return data;
    } catch (error) {
      pendingBidRef.current = null;
      recentBidRef.current = null;
      clearPendingTimers();
      setPendingBidState(null);
      if (error?.code === 'BIDDER_PROFILE_CANCELLED') {
        return null;
      }
      throw error;
    }
  }, [
    artworkData,
    clearPendingTimers,
    confirmPendingBid,
    markPendingBidWaiting,
    registerBidderDetails,
    userSession,
    isBiddingBlocked,
    isGalleryViewer,
    isArtworkCreator,
    isOwnArtwork,
    selectedPaymentMethodId,
    loadPaymentMethods,
    ensurePaymentProfile,
    paymentProfileStatus,
    handleAuthorizationChallenge,
  ]);

  return {
    artworkData,
    pendingBidState,
    handlePlaceBid,
    connectionBadge,
    isCountdownFrozen,
    serverTimeOffsetMs,
    isAuctionArtwork,
    isAuctionEnabled,
    isAuctionClosed,
    isOwnArtwork,
    isBiddingBlocked,
    paymentMethods,
    paymentMethodsStatus,
    paymentMethodsError,
    isLoadingPaymentMethods,
    hasPaymentMethods,
    selectedPaymentMethodId,
    setSelectedPaymentMethodId,
    loadPaymentMethods,
    refreshPaymentMethods,
    handleManagePaymentMethods,
  };
}
